export type SkipReason = 'moral_overload' | 'insufficient_info' | 'equally_valid' | 'other';

export const SKIP_REASONS: readonly SkipReason[] = [
  'moral_overload',
  'insufficient_info',
  'equally_valid',
  'other',
];

/**
 * §9a.3 reflection capture — confidence 0–100 and motivation tags. All fields
 * optional; omit the whole object to record an un-annotated response.
 */
export type MotivationTag =
  | 'fairness'
  | 'efficiency'
  | 'empathy'
  | 'duty'
  | 'pragmatism'
  | 'uncertainty';

export const MOTIVATION_TAGS: readonly MotivationTag[] = [
  'fairness',
  'efficiency',
  'empathy',
  'duty',
  'pragmatism',
  'uncertainty',
];

export interface Reflection {
  confidence?: number;
  motivationTags?: string[];
}

export type EmphasisTag =
  | 'packageCost'
  | 'capabilities'
  | 'storyKey'
  | 'baseline'
  | 'conflictKey'
  | 'nameHighlight';

export interface ScenarioView {
  index: number;
  total: number;
  isCalibration?: boolean;
  scenario: {
    id: string;
    block: string;
    decisionType: 'BINARY' | 'TRIAGE' | 'SPLIT' | 'POLICY';
    budget: number;
    flags: string[];
    promptKey: string;
    outcomeHorizonKeys: { T1: string; T5: string; T10: string };
    contrarianArgs: Array<{ choice: string; argument_key: string }>;
    candidates: Array<{
      candidateKey: string;
      choiceKey: string;
      archetypeId: string;
      name: string;
      storyKey: string | null;
      conflictKey: string | null;
      labelKey: string | null;
      capabilities: string[];
      packageCost: number;
      /** §3a Expert Mode: 12 extended attributes. Absent unless expert=1. */
      expertParams?: Record<string, number | string | string[]>;
      /** §3a G13 source codes for the extended attributes, keyed by attribute. */
      expertSources?: Record<string, string>;
    }>;
  };
  narrativeEmphasis?: EmphasisTag[];
  twist: { type: 'TARGETING_ERROR' } | { type: 'SHOCK'; multiplier: number } | null;
  showImc: boolean;
  progress: number;
  /** §24 Expert condition — echoed back when the client posted ?expert=1. */
  expertMode?: boolean;
}

export interface ResponseAck {
  accepted: boolean;
  outcomeT1Key: string;
  outcomeChoiceKey: string;
  outcomeFallbackKey?: string;
  flaggedFast: boolean;
  nextIndex: number;
  atEnd: boolean;
  forceNextNotRefuse: boolean;
  outcomeDeltas: Record<string, number>;
}

export interface DebriefTimelineEntry {
  scenarioId: string;
  scenarioIndex: number;
  choice: string;
  promptKey: string;
  horizons: Array<{ horizon: 'T1' | 'T5' | 'T10'; values: Record<string, number> }>;
  outcomeKeys: { T1: string; T5: string; T10: string };
}

export interface DebriefView {
  radar: Record<string, number>;
  topAxes: string[];
  inconsistencies: string[];
  school: string;
  schoolDistances: Record<string, number>;
  roleRevealed: {
    roleId: string;
    labelKey: string;
    descriptionKey: string;
    biasMatrix: Record<string, number> | null;
  };
  socialComparison: { similarPct: number; sampleSize: number } | null;
  timeline: DebriefTimelineEntry[];
  session: { completed: number; planned: number; flaggedCount: number; skipped: number };
}

async function send<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, body });
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export interface SessionSnapshot {
  sessionId: string;
  lang: string;
  total: number;
  progress: number;
  atEnd: boolean;
  twistAt: number;
  imcAt: number;
  needsOnboarding?: boolean;
}

export interface AnalyticsPayload {
  timezone?: string;
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
}

/**
 * §20 DPIA: собирает пассивные данные без permission-prompt —
 * timezone из `Intl.DateTimeFormat`, размеры экрана и вьюпорта из `window`.
 * Не трогает geolocation, canvas, WebGL и любые permission-gated API.
 * Возвращает только определённые поля: тестам и серверу важно уметь отличать
 * «не смогли» от «значение 0».
 */
export function collectClientAnalytics(): AnalyticsPayload {
  const out: AnalyticsPayload = {};
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0) out.timezone = tz;
  } catch {
    // Intl absent / blocked — skip.
  }
  if (typeof window !== 'undefined') {
    if (window.screen && Number.isFinite(window.screen.width)) out.screenWidth = window.screen.width;
    if (window.screen && Number.isFinite(window.screen.height)) out.screenHeight = window.screen.height;
    if (Number.isFinite(window.innerWidth)) out.viewportWidth = window.innerWidth;
    if (Number.isFinite(window.innerHeight)) out.viewportHeight = window.innerHeight;
  }
  return out;
}

export const api = {
  startSession(lang: string, consent: boolean, replayConsent?: boolean) {
    const analytics = collectClientAnalytics();
    return send<{ sessionId: string; lang: string; total: number; twistAt: number; imcAt: number }>(
      '/api/session',
      {
        method: 'POST',
        body: JSON.stringify({
          lang,
          consent,
          ...(replayConsent !== undefined ? { replayConsent } : {}),
          ...(Object.keys(analytics).length > 0 ? { analytics } : {}),
        }),
      },
    );
  },
  /** Resume after page reload. Returns null if no valid session cookie. */
  async currentSession(): Promise<SessionSnapshot | null> {
    const res = await fetch('/api/session', { credentials: 'include' });
    if (res.status === 204 || !res.ok) return null;
    return res.json() as Promise<SessionSnapshot>;
  },
  scenario(index: number, expertMode = false) {
    const qs = expertMode ? '?expert=1' : '';
    return send<ScenarioView>(`/api/scenario/${index}${qs}`);
  },
  submit(
    scenarioIndex: number,
    choice: string,
    timeToDecideMs: number,
    skipReason?: SkipReason,
    reflection?: Reflection,
    splitAllocation?: Record<string, number>,
  ) {
    return send<ResponseAck>('/api/response', {
      method: 'POST',
      body: JSON.stringify({
        scenarioIndex,
        choice,
        timeToDecideMs,
        skipReason,
        reflection,
        ...(splitAllocation ? { splitAllocation } : {}),
      }),
    });
  },
  debrief() {
    return send<DebriefView>('/api/debrief');
  },
  deleteSession() {
    return send<{ deleted: boolean }>('/api/session', { method: 'DELETE' });
  },
  submitDemographics(body: {
    region: string; ageBand: string; gender: string; education: string;
  }) {
    return send<{ stored: boolean }>('/api/demographics', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  submitImc(answer: 'continue' | 'skip' | 'random' | 'stop', scenarioIndex: number) {
    return send<{ passed: boolean }>('/api/imc', {
      method: 'POST',
      body: JSON.stringify({ answer, scenarioIndex }),
    });
  },
  submitOnboarding(answers: Record<string, string | string[]>, consentSensitive: boolean) {
    return send<OnboardingResult>('/api/onboarding', {
      method: 'POST',
      body: JSON.stringify({ answers, consentSensitive }),
    });
  },
  confirmOnboardingRole(pickedRoleId: string) {
    return send<{ roleId: string; locked: boolean }>('/api/onboarding/confirm', {
      method: 'POST',
      body: JSON.stringify({ pickedRoleId }),
    });
  },
  /**
   * §2a v2.3 — optional player identity profile. All fields optional;
   * a caller may submit any subset (e.g. only religion). Strings must
   * match VALID_RELIGIONS / VALID_ETHNICITIES or the server returns 400.
   */
  submitIdentity(body: {
    religion?: string;
    ethnicity?: string;
    citizenship_iso?: string;
  }) {
    return send<{ stored: boolean }>('/api/identity', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * §7a Phase L.8 — start a 10-scenario deep-dive from one v2.3 block.
   * Target is the class id: C25 (Downside), C26 (Upside), C27 (Identity).
   * The parent session must be completed before this endpoint accepts.
   */
  startDeepDive(target: 'C25' | 'C26' | 'C27') {
    return send<{
      sessionId: string;
      lang: string;
      total: number;
      isDeepDive: 'C25' | 'C26' | 'C27';
      parentSessionId: string;
    }>('/api/session/deep-dive', {
      method: 'POST',
      body: JSON.stringify({ target }),
    });
  },
};

export interface OnboardingTopRole {
  roleId: string;
  score: number;
  labelKey: string;
  descriptionKey: string;
}

export interface OnboardingResult {
  topRoles: OnboardingTopRole[];
  allRoles: Array<{ roleId: string; labelKey: string }>;
}
